import { createSlice } from "@reduxjs/toolkit";
import {
  requestInboundMailThunk,
  getCompanyInfoThunk,
  getCompaniesThunk,
  updateCompanyChannelsThunk,
} from "../actions/company.action";
import {
  ChannelFlags,
  DEFAULT_CHANNEL_FLAGS,
  ICompany,
  normalizeChannelFlags,
} from "../../models/company.model";

export interface CompanyState {
  companies: ICompany[];
  info: ICompany | null;
  channels: ChannelFlags;
  channelsLoaded: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: CompanyState = {
  companies: [],
  info: null,
  channels: DEFAULT_CHANNEL_FLAGS,
  channelsLoaded: false,
  loading: false,
  error: null,
};

const applyCompanyInfo = (state: CompanyState, company?: ICompany | null) => {
  if (!company) return;
  state.info = company;
  state.channels = normalizeChannelFlags(company.settings?.channels);
  state.channelsLoaded = true;
};

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestInboundMailThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestInboundMailThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(requestInboundMailThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(getCompanyInfoThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompanyInfoThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        applyCompanyInfo(state, action.payload?.data);
      })
      .addCase(getCompanyInfoThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.channelsLoaded = true;
      });
    builder
      .addCase(getCompaniesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompaniesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.companies = action.payload.data;
      })
      .addCase(getCompaniesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    builder
      .addCase(updateCompanyChannelsThunk.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCompanyChannelsThunk.fulfilled, (state, action) => {
        const next = action.payload?.data?.channels;
        if (next) {
          state.channels = normalizeChannelFlags(next);
          if (state.info) {
            state.info = {
              ...state.info,
              settings: {
                ...(state.info.settings || {}),
                channels: normalizeChannelFlags(next),
              },
            };
          }
        }
        state.channelsLoaded = true;
      })
      .addCase(updateCompanyChannelsThunk.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {} = companySlice.actions;
export default companySlice.reducer;
